import { useCallback, useMemo } from "react";
import {
	type SalesChannelCategoryGroupProductRow,
	useSalesChannelCategoryGroupProductList,
} from "./useSalesChannelCategoryGroupProductList";
import {
	type SalesChannelCategoryRow,
	useSalesChannelCategoryList,
} from "./useSalesChannelCategoryList";

export interface SalesChannelCategoryTreeNode {
	category: SalesChannelCategoryRow;
	products: SalesChannelCategoryGroupProductRow[];
}

/**
 * Loads the group's categories (roots) and products, nesting each product under
 * its salesChannelCategoryID. Reuses the existing category + product list
 * usecases — no extra queries.
 */
export const useSalesChannelCategoryGroupTree = (
	groupID: string | undefined,
	branchID?: string,
) => {
	const categories = useSalesChannelCategoryList(groupID);
	const products = useSalesChannelCategoryGroupProductList(groupID, branchID);

	const tree = useMemo<SalesChannelCategoryTreeNode[]>(() => {
		const byCategory = new Map<string, SalesChannelCategoryGroupProductRow[]>();
		for (const product of products.items) {
			const list = byCategory.get(product.salesChannelCategoryID) ?? [];
			list.push(product);
			byCategory.set(product.salesChannelCategoryID, list);
		}
		return categories.items.map((category) => ({
			category,
			products: byCategory.get(category.salesChannelCategoryID) ?? [],
		}));
	}, [categories.items, products.items]);

	const refetch = useCallback(() => {
		categories.refetch();
		products.refetch();
	}, [categories.refetch, products.refetch]);

	return {
		tree,
		categoryCount: categories.items.length,
		productCount: products.items.length,
		fetching: categories.fetching || products.fetching,
		stockError: products.error,
		refetch,
	};
};
